import { useState } from "react";
import { Button, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FilePdfOutlined, GlobalOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { SendReport } from "../../../../../libs/shared";
import {
  DashboardTable,
  PAGE_SIZE,
  PeriodT,
  usePdfDownload,
} from "./dashboard-table";

interface ProcessRowI {
  id?: number;
  container: string;
  startProcess?: string;
  endProcess?: string;
  userCreation?: string;
  createdAt?: string;
}

interface QueryParamsI {
  sorting: { id: string; desc: boolean }[];
  pagination: { pageIndex: number; pageSize: number };
  globalFilter: string;
  period: PeriodT;
}

export interface WorkflowProcessTablePropsI {
  useList: (params: QueryParamsI) => {
    data?: { data?: { dato?: any[]; total?: number } };
    isLoading: boolean;
    isRefetching: boolean;
    refetch: () => void;
  };
  generatePdf: (id: number) => Promise<{ data?: any }>;
  type: "one" | "two" | "three";
  number: number;
  ShowContainer: React.ComponentType<{ id: number }>;
}

const SORTING = [{ id: "id", desc: true }];

export const WorkflowProcessTable = ({
  useList,
  generatePdf,
  type,
  number,
  ShowContainer,
}: WorkflowProcessTablePropsI) => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<PeriodT>("hoy");
  const [pageIndex, setPageIndex] = useState(0);
  const [search, setSearch] = useState("");
  const { download, loadingId, contextHolder } = usePdfDownload(
    `Reporte-Proceso-${number}`
  );

  const { data, isLoading, isRefetching, refetch } = useList({
    sorting: SORTING,
    pagination: { pageIndex, pageSize: PAGE_SIZE },
    globalFilter: search,
    period,
  });

  const columns: ColumnsType<ProcessRowI> = [
    {
      title: "N° Contenedor",
      dataIndex: "container",
      render: (value, row) => (
        <a onClick={() => navigate(`/workflow/${type}/edit/${row.id}`)}>
          {value}
        </a>
      ),
    },
    {
      title: "Hora Inicio",
      dataIndex: "startProcess",
      responsive: ["sm"],
    },
    {
      title: "Hora Final",
      dataIndex: "endProcess",
      responsive: ["sm"],
    },
    {
      title: "Creado por",
      dataIndex: "userCreation",
    },
    {
      title: "Fecha de creación",
      dataIndex: "createdAt",
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, row) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Button
            size="small"
            danger
            icon={<FilePdfOutlined />}
            loading={loadingId === row.id}
            disabled={loadingId !== null}
            onClick={() => download(generatePdf, row.id!, row.container)}
          />
          <SendReport id={row.id!} typeWf={type} />
          <ShowContainer id={row.id!} />
          <Tooltip arrow={false} title="Link Externo">
            <Button
              size="small"
              type="dashed"
              icon={<GlobalOutlined />}
              onClick={() => navigate(`/exterior/workflow-${type}/${row.id}`)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <DashboardTable<ProcessRowI>
        columns={columns}
        data={data?.data?.dato ?? []}
        total={data?.data?.total ?? 0}
        loading={isLoading || isRefetching}
        pageIndex={pageIndex}
        period={period}
        onPeriodChange={setPeriod}
        onPageChange={setPageIndex}
        onSearch={setSearch}
        onRefresh={refetch}
      />
    </>
  );
};
